import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Howl } from 'howler';
import { addToWhitelist } from '../services/firebase';
import PrizeRevealModal from '../components/PrizeRevealModal';

const MACHINE_STYLE = {
  brandColor: '#C12127',
  accent: '#21476B',
  hopperCapsules: [
    ['#F5F8FF', '#F0463D'],
    ['#F5F8FF', '#2BA4B4'],
    ['#F5F8FF', '#F6C34E'],
  ],
  dropCapsule: ['#F5F8FF', '#2BA4B4'],
};

const MAX_SPINS = 1; // CHANGED: Each user gets 1 spin

// CHANGED: Fixed prize pool for Anime Vegas demo
// Real 1/1, Token 50%/50 stock, Empty remainder (49/49) — total 100 capsules
const ANIME_VEGAS_PRIZE_TYPES = [
  // CHANGED: Adjust weights/stocks to staff-controlled batch restock
  { type: 'real', weight: 1, stock: 1, name: 'Real Gachapon', imageUrl: '/assets/yellow_gacha.png' },
  { type: 'token', weight: 50, stock: 50, name: 'PonPon Token', imageUrl: '/assets/token-icon.png' },
  { type: 'empty', weight: 49, stock: 49, name: 'Sorry!', image: '😅' },
];

// Track remaining stock (in localStorage for persistence across page refreshes)
const getStockKey = (type) => `ponpon:animevegas:stock:${type}`;

const getRemainingStock = (type) => {
  const stored = localStorage.getItem(getStockKey(type));
  return stored ? parseInt(stored, 10) : ANIME_VEGAS_PRIZE_TYPES.find((t) => t.type === type)?.stock || 0;
};

const setRemainingStock = (type, stock) => {
  localStorage.setItem(getStockKey(type), stock.toString());
};

// Initialize stock if not set
const initializeStock = () => {
  ANIME_VEGAS_PRIZE_TYPES.forEach(({ type, stock }) => {
    if (localStorage.getItem(getStockKey(type)) === null) {
      setRemainingStock(type, stock);
    }
  });
};

// CHANGED: Reset stock to initial values
const resetStock = () => {
  ANIME_VEGAS_PRIZE_TYPES.forEach(({ type, stock }) => {
    setRemainingStock(type, stock);
  });
};

// CHANGED: Select prize without decreasing stock (stock will be decreased when capsule is opened)
const selectWeightedPrize = () => {
  // Filter out prize types with no stock
  const availableTypes = ANIME_VEGAS_PRIZE_TYPES.filter(({ type }) => getRemainingStock(type) > 0);

  if (availableTypes.length === 0) {
    // All stock depleted, return sorry
    return { type: 'empty', name: 'Sorry!', image: '😅', stock: 0 };
  }

  // Calculate total weight of available types
  const totalWeight = availableTypes.reduce((sum, t) => sum + t.weight, 0);
  let random = Math.random() * totalWeight;

  // Select prize type based on weight
  let selectedType = null;
  for (const prizeType of availableTypes) {
    random -= prizeType.weight;
    if (random <= 0) {
      selectedType = prizeType;
      break;
    }
  }

  // Fallback to first available type
  if (!selectedType) {
    selectedType = availableTypes[0];
  }

  // CHANGED: Don't decrease stock here - it will be decreased when capsule is opened
  const remainingStock = getRemainingStock(selectedType.type);

  // Generate unique ID based on type and remaining stock
  const id = `${selectedType.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  return {
    id,
    type: selectedType.type,
    name: selectedType.name,
    image: selectedType.image,
    imageUrl: selectedType.imageUrl || null,
    stock: remainingStock, // CHANGED: Return current stock, not decreased stock
  };
};

// CHANGED: One-page version for Anime Vegas iPad demo
const AnimeVegasPage = () => {
  // Flow state: 'form' | 'spin' | 'complete'
  const [flowStage, setFlowStage] = useState(() => {
    const isWhitelisted = localStorage.getItem('ponpon:animevegas:isWhitelisted');
    const spinsUsed = parseInt(localStorage.getItem('ponpon:animevegas:spinsUsed') || '0', 10);
    if (isWhitelisted && spinsUsed < MAX_SPINS) {
      return 'spin';
    }
    if (isWhitelisted && spinsUsed >= MAX_SPINS) {
      return 'complete';
    }
    return 'form';
  });

  // Spin state
  const [spinsUsed, setSpinsUsed] = useState(() => {
    const stored = localStorage.getItem('ponpon:animevegas:spinsUsed');
    return stored ? parseInt(stored, 10) : 0;
  });
  // CHANGED: Use fallback machine data, no Firestore dependency
  const [selectedMachine] = useState({
    id: 'animevegas-demo',
    name: 'Anime Vegas Demo',
    brand: 'PonPon',
    tagline: 'Experience real Gachapon online',
    imageUrl: '/assets/ponpon-fallback.svg', // CHANGED: Use PonPon fallback machine image
    tokenCost: 3, // CHANGED: Requires 3 tokens to spin
    tags: ['Demo', 'Anime Vegas'],
    prizePool: ANIME_VEGAS_PRIZE_TYPES,
    inventoryAvailable: 100,
    inventoryTotal: 100,
  });
  const [isSpinning, setIsSpinning] = useState(false);
  const [prize, setPrize] = useState(null);
  const [tokensInserted, setTokensInserted] = useState(0);
  const [capsuleOpened, setCapsuleOpened] = useState(false);
  const [dropComplete, setDropComplete] = useState(true);
  const [showPrizeChute, setShowPrizeChute] = useState(false);
  const [isAwaitingPrize, setIsAwaitingPrize] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const [activeCapsuleIndex, setActiveCapsuleIndex] = useState(0);

  const spinSoundRef = useRef(null);
  const coinSoundRef = useRef(null);
  const heartbeatSoundRef = useRef(null);
  const revealSoundRef = useRef(null);
  const dropSoundRef = useRef(null);
  const prizeDisplayRef = useRef(null);
  const revealTimeoutRef = useRef(null);
  const [isPrizeModalOpen, setIsPrizeModalOpen] = useState(false);
  const [prizeModalOrigin, setPrizeModalOrigin] = useState(null);

  const spinsRemaining = MAX_SPINS - spinsUsed;
  const isComplete = spinsRemaining <= 0;

  // Initialize audio
  useEffect(() => {
    spinSoundRef.current = new Howl({
      src: ['/audio/spinsound.mp3'],
      volume: 0.7,
      html5: true,
    });
    coinSoundRef.current = new Howl({
      src: ['/audio/coinsound.mp3'],
      volume: 0.6,
      html5: true,
    });
    heartbeatSoundRef.current = new Howl({
      src: ['/audio/heartbeatsound.mp3'],
      volume: 0.5,
      html5: true,
      loop: true,
    });
    revealSoundRef.current = new Howl({
      src: ['/audio/revealsound.mp3'],
      volume: 0.7,
      html5: true,
    });
    dropSoundRef.current = new Howl({
      src: ['/audio/dropsound.mp3'],
      volume: 0.6,
      html5: true,
    });
    return () => {
      spinSoundRef.current?.unload();
      coinSoundRef.current?.unload();
      heartbeatSoundRef.current?.unload();
      revealSoundRef.current?.unload();
      dropSoundRef.current?.unload();
    };
  }, []);

  // CHANGED: Track stock changes to trigger re-renders
  const [stockVersion, setStockVersion] = useState(0);

  // CHANGED: Initialize stock on mount
  useEffect(() => {
    initializeStock();
    setStockVersion((prev) => prev + 1); // Trigger initial calculation
  }, []);

  // Auto-advance to complete stage
  useEffect(() => {
    if (isComplete && !isSpinning && !prize && flowStage === 'spin') {
      const timer = setTimeout(() => {
        setFlowStage('complete');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isComplete, isSpinning, prize, flowStage]);

  // Update capsule index during spin
  useEffect(() => {
    if (isSpinning) {
      setActiveCapsuleIndex(Math.floor(Math.random() * MACHINE_STYLE.hopperCapsules.length));
    }
  }, [isSpinning]);

  // Reset prize state
  useEffect(() => {
    if (!isSpinning && !prize) {
      setDropComplete(true);
      setShowPrizeChute(false);
    }
  }, [isSpinning, prize]);

  // Heartbeat sound when capsule jittering
  useEffect(() => {
    const shouldJitter = showPrizeChute && dropComplete && prize && !capsuleOpened;
    const heartbeatSound = heartbeatSoundRef.current;
    if (heartbeatSound) {
      if (shouldJitter) {
        if (!heartbeatSound.playing()) {
          heartbeatSound.play();
        }
      } else {
        if (heartbeatSound.playing()) {
          heartbeatSound.stop();
        }
      }
    }
  }, [showPrizeChute, dropComplete, prize, capsuleOpened]);

  const capsuleStyle = useMemo(
    () => MACHINE_STYLE.hopperCapsules[activeCapsuleIndex] ?? MACHINE_STYLE.dropCapsule,
    [activeCapsuleIndex],
  );

  const tokenCost = selectedMachine?.tokenCost ?? 1;
  const isFullyInserted = tokensInserted >= tokenCost;
  const canSpin = !isComplete && !isSpinning && isFullyInserted && dropComplete;

  // CHANGED: Simple CTA handler – mark as started and go to spin page
  const handleStartSpin = () => {
    localStorage.setItem('ponpon:animevegas:isWhitelisted', 'true');
    if (!localStorage.getItem('ponpon:animevegas:spinsUsed')) {
      localStorage.setItem('ponpon:animevegas:spinsUsed', '0');
    }
    setFlowStage('spin');
  };

  // Spin handlers
  const handleInsertToken = useCallback(() => {
    if (isComplete || isSpinning || !dropComplete || isFullyInserted) return;
    setCapsuleOpened(false);
    setTokensInserted((prev) => prev + 1);
    try {
      const coinSound = coinSoundRef.current;
      if (coinSound) {
        if (coinSound.playing()) {
          coinSound.stop();
        }
        coinSound.play();
      }
    } catch (e) {}
  }, [isComplete, isSpinning, dropComplete, isFullyInserted]);

  const handleKnobClick = useCallback(() => {
    if (!canSpin || !selectedMachine) return;

    try {
      const spinSound = spinSoundRef.current;
      if (spinSound) {
        if (spinSound.playing()) {
          spinSound.stop();
        }
        spinSound.play();
      }
    } catch (e) {}

    setTokensInserted(0);
    setCapsuleOpened(false);
    setIsAwaitingPrize(true);
    setShowPrizeChute(true);
    setDropComplete(false);
    setIsSpinning(true);

    setTimeout(() => {
      try {
        const dropSound = dropSoundRef.current;
        if (dropSound) {
          if (dropSound.playing()) {
            dropSound.stop();
          }
          dropSound.play();
        }
      } catch (e) {}
    }, 1100);
  }, [canSpin, selectedMachine]);

  const handleDropAnimationEnd = useCallback(() => {
    setDropComplete(true);
    setShowPrizeChute(true);
    setIsAwaitingPrize(true);

    const selectedPrize = selectWeightedPrize();
    setPrize({
      id: `prize-${Date.now()}`,
      name: selectedPrize.name,
      image: selectedPrize.image,
      imageUrl: selectedPrize.imageUrl || null,
      type: selectedPrize.type,
    });
    setIsSpinning(false);
    // CHANGED: Stock will be decreased when capsule is opened, not here
  }, []);

  const handleCapsuleOpen = useCallback(() => {
    if (capsuleOpened || isPrizeModalOpen || !prize) return;

    try {
      heartbeatSoundRef.current?.stop();
      const revealSound = revealSoundRef.current;
      if (revealSound) {
        if (revealSound.playing()) {
          revealSound.stop();
        }
        revealSound.play();
      }
    } catch (e) {}

    setCapsuleOpened(true);
    setShowFireworks(true);
    setTimeout(() => setShowFireworks(false), 1300);

    const remainingStock = getRemainingStock(prize.type);
    if (remainingStock > 0) {
      setRemainingStock(prize.type, remainingStock - 1);
      setStockVersion((prev) => prev + 1);
    }

    const newSpinsUsed = spinsUsed + 1;
    setSpinsUsed(newSpinsUsed);
    localStorage.setItem('ponpon:animevegas:spinsUsed', newSpinsUsed.toString());

    const savedEmail = localStorage.getItem('ponpon:animevegas:email');
    const savedNickname = localStorage.getItem('ponpon:animevegas:nickname');
    const savedFavoriteAnime = localStorage.getItem('ponpon:animevegas:favoriteAnime');

    if (savedEmail && savedNickname) {
      const spinResult = {
        spinNumber: newSpinsUsed,
        prizeType: prize.type,
        prizeName: prize.name || '',
        prizeImage: prize.image || '',
        timestamp: new Date().toISOString(),
      };

      addToWhitelist({
        email: savedEmail,
        nickname: savedNickname,
        favoriteAnime: savedFavoriteAnime || '',
        spinsUsed: newSpinsUsed,
        spinResult,
      }).catch(console.error);
    }

    const computeOrigin = () => {
      const rect = prizeDisplayRef.current ? prizeDisplayRef.current.getBoundingClientRect() : null;
      if (!rect || typeof window === 'undefined') {
        setPrizeModalOrigin(null);
        return;
      }
      setPrizeModalOrigin({
        x: rect.left + rect.width / 2 - window.innerWidth / 2,
        y: rect.top + rect.height / 2 - window.innerHeight / 2,
      });
    };

    // CHANGED: Let capsule open animation play before showing modal
    if (revealTimeoutRef.current) {
      clearTimeout(revealTimeoutRef.current);
      revealTimeoutRef.current = null;
    }
    revealTimeoutRef.current = setTimeout(() => {
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(computeOrigin);
      } else {
        computeOrigin();
      }
      setIsPrizeModalOpen(true);
      revealTimeoutRef.current = null;
    }, 900);
  }, [capsuleOpened, isPrizeModalOpen, prize, spinsUsed]);

  const handlePrizeModalClose = useCallback(() => {
    setIsPrizeModalOpen(false);
    setCapsuleOpened(false);
    setShowPrizeChute(false);
    setIsAwaitingPrize(false);
    setPrize(null);
    setPrizeModalOrigin(null);
    // CHANGED: Move to next step immediately after clicking Next
    setFlowStage('complete');
  }, []);

  // CHANGED: Calculate inventory and prize info for detail panel (must be before early return)
  // CHANGED: Calculate actual remaining inventory from all prize types
  const totalRemainingStock = useMemo(() => {
    // CHANGED: depend on stockVersion so total remaining updates when stock changes
    // eslint-disable-next-line no-unused-expressions
    stockVersion;
    return ANIME_VEGAS_PRIZE_TYPES.reduce((sum, prizeType) => {
      return sum + getRemainingStock(prizeType.type);
    }, 0);
  }, [stockVersion]);

  const totalInitialStock = ANIME_VEGAS_PRIZE_TYPES.reduce((sum, prizeType) => sum + prizeType.stock, 0);

  const inventoryPercent = useMemo(() => {
    if (totalInitialStock <= 0) return 0;
    return Math.max(0, Math.min(100, (totalRemainingStock / totalInitialStock) * 100));
  }, [totalRemainingStock, totalInitialStock]);

  const machineSoldOut = totalRemainingStock <= 0;
  const lowInventory =
    !machineSoldOut && totalRemainingStock <= Math.max(5, Math.round(totalInitialStock * 0.1));

  // CHANGED: Use ANIME_VEGAS_PRIZE_TYPES for detail panel display
  // CHANGED: Add stockVersion to dependencies to ensure it updates when stock changes
  const brightPicks = useMemo(() => {
    // CHANGED: depend on stockVersion so inventory display updates when stock changes
    // eslint-disable-next-line no-unused-expressions
    stockVersion;
    return ANIME_VEGAS_PRIZE_TYPES.map((prizeType) => {
      const remainingStock = getRemainingStock(prizeType.type);
      return {
        id: prizeType.type,
        name: prizeType.name,
        imageUrl: prizeType.imageUrl || null,
        weightPercent: prizeType.weight,
        image: prizeType.image,
        stock: remainingStock,
        totalStock: prizeType.stock,
      };
    });
  }, [stockVersion]);

  // Render based on flow stage
  if (flowStage === 'form') {
    return (
      <div className="fixed inset-0 bg-gradient-to-b from-ponpon-coral/10 via-white to-ponpon-teal/10">
        <div className="h-full overflow-y-auto">
          <div className="flex min-h-full flex-col px-4 py-8">
            <div className="w-full mx-auto">
              {/* Logo - At the top, expanded */}
              <div className="mb-8 flex justify-center pt-4">
                <img
                  src="/assets/ponpon-logo.svg"
                  alt="PonPon"
                  className="max-h-48 max-w-full sm:max-h-64 md:max-h-80 object-contain"
                />
              </div>

              {/* CHANGED: Replace hero text with QR + CTA only */}
              <div className="mx-auto max-w-3xl flex flex-col items-center gap-8">
                <h2 className="text-center text-2xl font-bold text-ponpon-navy sm:text-3xl">
                  Follow PonPon & Get Ready to Spin
                </h2>
                <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                  <div className="rounded-2xl bg-white p-4 shadow-lg w-56 h-56 sm:w-64 sm:h-64 aspect-square flex items-center justify-center">
                    <img
                      src="/assets/instagram.png"
                      alt="Instagram QR Code"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="rounded-2xl bg-white p-4 shadow-lg w-56 h-56 sm:w-64 sm:h-64 aspect-square flex items-center justify-center">
                    <img
                      src="/assets/tiktok.png"
                      alt="TikTok QR Code"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleStartSpin}
                  className="mt-2 inline-flex items-center justify-center rounded-full bg-ponpon-coral px-8 py-3 text-base font-semibold text-white shadow-[0_12px_26px_rgba(226,58,50,0.28)] transition hover:-translate-y-0.5 hover:bg-[#E23A32] active:translate-y-0 active:shadow-md"
                >
                  GET YOUR SPIN
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* CHANGED: Reset stock button - small, bottom right, ghost style */}
        <button
          onClick={() => {
            resetStock();
            setStockVersion((prev) => prev + 1);
            alert('Stock reset!');
          }}
          className="fixed bottom-4 right-4 rounded-full border border-ponpon-navy/30 bg-transparent px-3 py-2 text-xs font-medium text-ponpon-navy/70 hover:border-ponpon-navy/50 hover:bg-ponpon-navy/10 hover:text-ponpon-navy transition-all"
          title="Reset Stock"
        >
          Reset Stock
        </button>
      </div>
    );
  }

  if (flowStage === 'spin') {
    return (
      <div className="fixed inset-0 bg-gradient-to-b from-ponpon-coral/10 via-white to-ponpon-teal/10">
        <div className="h-full overflow-y-auto">
          <div className="mx-auto max-w-6xl px-4 py-8">
            {/* CHANGED: Use machine modal structure from Homepage, no extra header to save vertical space */}
            <div className="w-full">
              <div
                className="machine-modal-dialog"
                role="dialog"
                aria-modal="true"
                style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '920px',
                  margin: '0 auto',
                  height: 'auto',
                  maxHeight: 'none',
                  // CHANGED: Override CSS variables that would normally come from .machine-modal
                  '--machine-modal-scale': '1',
                  '--machine-modal-pad-x': '0',
                  '--machine-modal-available-height': '100vh',
                }}
              >
                <section className="rounded-[36px] border border-white/60 bg-white/90 p-6 shadow-xl backdrop-blur-lg">
                  <div className="machine-detail-row">
                    {/* Machine */}
                    <div className="machine-experience">
                      <div className="machine-unit machine-unit-large relative">
                        <div className="machine-top" style={{ borderColor: `${MACHINE_STYLE.accent}33` }}>
                          <div className="machine-brand" style={{ color: MACHINE_STYLE.brandColor }}>
                            {selectedMachine.brand || 'PonPon'}
                          </div>
                        </div>
                        <div className="machine-glass">
                          <div
                            className="machine-primary-image flex h-full w-full items-center justify-center bg-white/70"
                            aria-hidden="true"
                          >
                            {selectedMachine.imageUrl ? (
                              <img
                                src={selectedMachine.imageUrl}
                                alt={selectedMachine.name}
                                className="max-h-full max-w-full object-contain"
                              />
                            ) : (
                              <div className="text-6xl">🎁</div>
                            )}
                          </div>
                        </div>
                        <div className="machine-body-shell">
                          <div className="machine-insert-panel">
                            <div className="machine-label" style={{ color: MACHINE_STYLE.accent }}>
                              Insert Token
                            </div>
                            <button
                              type="button"
                              onClick={handleInsertToken}
                              disabled={isComplete || isSpinning || !dropComplete || isFullyInserted}
                              className={`machine-coin-slot ${
                                isFullyInserted ? 'token-inserted' : ''
                              } ${
                                !isFullyInserted && !isComplete && dropComplete ? 'nudge-shake' : ''
                              }`}
                            >
                              {!isFullyInserted && (
                                <span className="machine-coin-icon" aria-hidden="true">
                                  <img
                                    src="/assets/token-icon.png"
                                    alt=""
                                    className="w-5 h-5 object-contain"
                                  />
                                </span>
                              )}
                              <span className="machine-token-text">
                                {isFullyInserted ? (
                                  <strong>Ready!</strong>
                                ) : (
                                  <strong>
                                    {tokensInserted}/{tokenCost}
                                  </strong>
                                )}
                              </span>
                            </button>
                            <div className="machine-slot-rails" aria-hidden="true">
                              <span className="slot-rail slot-rail-left" />
                              <span className="slot-rail slot-rail-right" />
                            </div>
                          </div>
                          <div className={`machine-knob-axis ${isSpinning ? 'is-rotating' : ''}`}>
                            <button
                              type="button"
                              onClick={handleKnobClick}
                              disabled={!canSpin}
                              className={`machine-knob ${!canSpin ? 'is-disabled' : ''} ${
                                isFullyInserted && !isSpinning ? 'is-armed' : ''
                              }`}
                              aria-label="Turn dial to spin"
                            >
                              <span className="machine-knob-face" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                        <div
                          className="machine-chute"
                          style={{
                            minHeight: '150px', // CHANGED: Ensure chute always tall enough for one capsule
                          }}
                        >
                          {!dropComplete && (
                            <div
                              className="active-drop-capsule"
                              style={{
                                '--capsule-top': capsuleStyle[0],
                                '--capsule-bottom': capsuleStyle[1],
                              }}
                              onAnimationEnd={handleDropAnimationEnd}
                            >
                              <div className="active-drop-shell" />
                            </div>
                          )}
                          {showPrizeChute && dropComplete && (!isSpinning || isAwaitingPrize) ? (
                            <div
                              className={`chute-ready ${capsuleOpened ? 'opened' : ''}`}
                              style={{
                                '--capsule-top': capsuleStyle[0],
                                '--capsule-bottom': capsuleStyle[1],
                              }}
                            >
                              <button
                                type="button"
                                onClick={handleCapsuleOpen}
                                disabled={!prize || capsuleOpened}
                                className={`chute-capsule-trigger ${
                                  capsuleOpened ? 'opened' : ''
                                }`}
                              >
                                <div
                                  className={`chute-capsule-shell ${
                                    capsuleOpened ? 'open' : ''
                                  } ${!capsuleOpened && prize ? 'capsule-jitter' : ''}`}
                                />
                              </button>
                              {capsuleOpened && prize ? (
                                <div className="chute-prize-display reveal" ref={prizeDisplayRef}>
                                  {prize.type === 'empty' ? (
                                    <span className="text-4xl">😅</span>
                                  ) : prize.imageUrl ? (
                                    <img
                                      src={prize.imageUrl}
                                      alt={prize.name}
                                      className="max-h-24 max-w-[70%] object-contain"
                                    />
                                  ) : (
                                    <span className="text-4xl sm:text-5xl">
                                      {prize.image || '🎁'}
                                    </span>
                                  )}
                                </div>
                              ) : null}
                              {showFireworks ? (
                                <div className="fireworks" aria-hidden="true">
                                  {[...Array(12)].map((_, i) => (
                                    <span key={i} className="spark" />
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    {/* CHANGED: Detail Panel */}
                    {selectedMachine && (
                      <aside className="machine-info-panel">
                        <div className="machine-info-header">
                          <div className="machine-tag-row">
                            {selectedMachine.tags && selectedMachine.tags.length > 0 ? (
                              selectedMachine.tags.map((tag) => (
                                <span key={tag} className="machine-tag">
                                  {tag}
                                </span>
                              ))
                            ) : (
                              <span className="machine-tag">PonPon</span>
                            )}
                          </div>
                          <h3 className="machine-info-title">
                            {selectedMachine.name || 'PonPon Gachapon'}
                          </h3>
                          {selectedMachine.tagline ? (
                            <p className="machine-info-subtitle">{selectedMachine.tagline}</p>
                          ) : null}
                        </div>

                        {/* CHANGED: Show inventory based on actual stock for staff restock tracking */}
                        <div className="machine-info-inventory" aria-label="Inventory status">
                          <div
                            className="inventory-progress"
                            role="progressbar"
                            aria-valuenow={totalRemainingStock}
                            aria-valuemin={0}
                            aria-valuemax={totalInitialStock}
                          >
                            <div
                              className={`inventory-progress-fill ${
                                machineSoldOut ? 'sold-out' : lowInventory ? 'low-stock' : ''
                              }`}
                              style={{ width: `${inventoryPercent}%` }}
                            />
                          </div>
                          <p className="inventory-count">
                            {totalRemainingStock} of {totalInitialStock} capsules remaining
                          </p>
                        </div>

                        <div className="machine-info-prizes">
                          <h4>What's Inside</h4>
                          <div className="prize-grid">
                            {brightPicks.length > 0 ? (
                              brightPicks.map(
                                ({ id, name, imageUrl, weightPercent, image }) => (
                                  <div key={id || name} className="prize-card">
                                    <div className="prize-card-thumb">
                                      {imageUrl ? (
                                        <img src={imageUrl} alt={name} />
                                      ) : image ? (
                                        <div className="flex items-center justify-center text-4xl">
                                          {image}
                                        </div>
                                      ) : (
                                        <div className="prize-card-placeholder" />
                                      )}
                                    </div>
                                    <div className="prize-card-name">{name}</div>
                                  </div>
                                ),
                              )
                            ) : (
                              <p className="text-sm text-ponpon-navy/60">No prizes available</p>
                            )}
                          </div>
                        </div>
                      </aside>
                    )}
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
        <PrizeRevealModal
          isOpen={isPrizeModalOpen}
          prize={prize}
          onClose={handlePrizeModalClose}
          onViewLocker={handlePrizeModalClose}
          onBackToHomepage={handlePrizeModalClose}
          originOffset={prizeModalOrigin}
          demoMode
          titleOverride={prize?.type === 'empty' ? 'Aww, not this time!' : 'Congratulations!'}
          messageOverride={
            prize?.type === 'empty'
              ? ''
              : (
                <>
                  You've got <strong>{prize?.name || 'Mystery prize'}</strong>，Join PonPon! to redeem your prize!
                </>
              )
          }
          demoCtaLabel="Next"
        />
      </div>
    );
  }

  // Complete stage
  const handleReset = () => {
    // CHANGED: Clear all localStorage items related to Anime Vegas flow
    // NOTE: Stock is NOT reset here because it represents real limited inventory
    localStorage.removeItem('ponpon:animevegas:isWhitelisted');
    localStorage.removeItem('ponpon:animevegas:email');
    localStorage.removeItem('ponpon:animevegas:nickname');
    localStorage.removeItem('ponpon:animevegas:favoriteAnime');
    localStorage.removeItem('ponpon:animevegas:spinsUsed');

    // Clear sessionStorage (if any)
    sessionStorage.removeItem('ponpon:animevegas:isWhitelisted');
    sessionStorage.removeItem('ponpon:animevegas:email');
    sessionStorage.removeItem('ponpon:animevegas:nickname');
    sessionStorage.removeItem('ponpon:animevegas:favoriteAnime');
    sessionStorage.removeItem('ponpon:animevegas:spinsUsed');

    // Stop all audio
    spinSoundRef.current?.stop();
    coinSoundRef.current?.stop();
    heartbeatSoundRef.current?.stop();
    revealSoundRef.current?.stop();
    dropSoundRef.current?.stop();

    // Clear any pending timeouts
    if (revealTimeoutRef.current) {
      clearTimeout(revealTimeoutRef.current);
      revealTimeoutRef.current = null;
    }

    // Reset all spin state
    setSpinsUsed(0);
    // CHANGED: selectedMachine is const, no need to reset
    setIsSpinning(false);
    setPrize(null);
    setTokensInserted(0);
    setCapsuleOpened(false);
    setDropComplete(true);
    setShowPrizeChute(false);
    setIsAwaitingPrize(false);
    setShowFireworks(false);
    setActiveCapsuleIndex(0);

    // Reset flow stage
    setFlowStage('form');
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-ponpon-coral/10 via-white to-ponpon-teal/10">
      <div className="h-full overflow-y-auto">
        <div className="flex min-h-full items-center justify-center px-4 py-8">
          <div className="w-full max-w-2xl text-center">
            {/* CHANGED: Remove bouncing confetti for cleaner final step */}
            <h1 className="mb-4 text-4xl font-bold text-ponpon-navy sm:text-5xl">Join PonPon</h1>
            <p className="mb-8 text-lg text-ponpon-navy/70 sm:text-xl">
              Explore more Gachapon series
            </p>

            {/* CHANGED: Keep QR placement, remove Instagram/TikTok images */}
            <div className="mb-8 flex justify-center gap-4">
              <div className="rounded-2xl bg-white p-4 shadow-lg w-64 h-64 sm:w-80 sm:h-80 aspect-square flex items-center justify-center">
                <img
                  src="/assets/ponpon-qrcode.png"
                  alt="PonPon QR Code"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            {/* Back to Landing Page Link */}
            <div className="mb-8">
              <button
                onClick={handleReset}
                className="text-ponpon-navy/70 underline hover:text-ponpon-navy transition"
              >
                ← Back to landing page
              </button>
            </div>

            <footer className="mt-12 border-t border-ponpon-navy/10 pt-6">
              <p className="text-sm text-ponpon-navy/60">
                PonPon! © 2026 | All rights reserved
              </p>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimeVegasPage;

